import config from "config";
import useCustomLoginErrorHandler from "hooks/useCustomLoginErrorHandler";
import Form from "components/Reusable/Form/Form.js";
import signInFields from "./helpers/signInFields";
import clsx from "clsx";
import useMfa from "hooks/useMfa";

import { useToasts } from "react-toast-notifications";
import { useContent } from "contexts/contentContext/contentContext";
import { useMail } from "contexts/mailContext/mailContext";
import { useModal } from "contexts/modalContext/modalContext";
import { useUser } from "contexts/userContext/userContext";
import { ReactIsMailSavedByUserApiRequest } from "api/MailsController";
import { CustomDomainPagesE } from "models/enums";

import { memo, useRef, useState } from "react";

import styles from "./loginForm.module.scss";

interface LoginFormI extends React.HTMLAttributes<HTMLFormElement> {
  email?: string;
  onSubmitSuccess?: (...args: unknown[]) => void;
  onSubUserSubmitSuccess?: (email: string) => void;
  submitTextKey?: string;
  setEmailNotBelongToOrg?: (email: string) => void;
}

const isInviteByLinkPage = window.location.pathname.includes(
  CustomDomainPagesE.INVITE
);
const isInviteByEmailPage = window.location.pathname.includes(
  CustomDomainPagesE.SUB_USER_COMPLETE
);

const LoginForm = memo((props: LoginFormI) => {
  const {
    email,
    onSubmitSuccess,
    onSubUserSubmitSuccess,
    submitTextKey = "login-to-jumbomail",
    className,
    setEmailNotBelongToOrg,
    ...restProps
  } = props;

  const { closeModal } = useModal();
  const { getTextByKey, getCurrentPage, getPath } = useContent();
  const { getMailId, setMetaDataProp } = useMail();
  const { login } = useUser();
  const { addToast } = useToasts();
  const [emailField, setEmailField] = useState(email || "");
  const { openMfaLoginModal, generateSessionId } = useMfa();

  const externalRecaptchaRef = useRef(null);

  const handleCustomError = useCustomLoginErrorHandler();

  const handleSubmit = async (body: {
    Email: string;
    Password: string;
    Remember: boolean;
    recaptchaResponse: string;
    captchaServiceName: string;
    SessionId?: string;
  }) => {
    setEmailField(body.Email);

    const sessionId = body.SessionId || generateSessionId();

    const bodyDTO = {
      UserName: body.Email,
      Password: body.Password,
      Remember: body.Remember,
      RecaptchaResponse: body.recaptchaResponse,
      captchaServiceName: body.captchaServiceName,
      SessionId: sessionId,
    };

    const response = await login(bodyDTO);

    if (response.status === "success") {
      addToast(getTextByKey("sign-in-success"), {
        appearance: response.status,
      });
      closeModal();

      if (onSubmitSuccess) {
        onSubmitSuccess(response.message);
      }

      const currPage = getCurrentPage();

      if (currPage === "registration" || currPage === "login") {
        window.location.href = `${config.baseurl}${getPath()}home`;
      }

      if (currPage === "reset-password") {
        window.location.pathname = `${getPath()}home`;
      }

      if (currPage === "gallery") {
        const mailId = getMailId();

        const res = await ReactIsMailSavedByUserApiRequest(mailId);

        if (res.status === "success") {
          setMetaDataProp("IsSavedByUser", res.data);
        }
      }

      if (isInviteByLinkPage && onSubUserSubmitSuccess) {
        await onSubUserSubmitSuccess(body.Email);
      }

      if (isInviteByEmailPage && onSubUserSubmitSuccess) {
        await onSubUserSubmitSuccess(body.Email);
      }
    } else {
      if (response.message === "not_under_domain") {
        setEmailNotBelongToOrg(body.Email);
        return;
      }

      if (
        response.status === "error" &&
        response.message === "required-mfa-code"
      ) {
        const onMFACodeCheck = async (
          Code: string,
          _: any,
          __: any,
          recaptchaResponse: string
        ) => {
          const mfaBody = {
            ...bodyDTO,
            Code,
            recaptchaResponse,
          };

          const mfaResponse = await login(mfaBody);

          if (
            mfaResponse.status === "error" &&
            JSON.stringify(mfaResponse.message).includes("wrong-mfa-code")
          ) {
            return getTextByKey("wrong-mfa-code");
          } else {
            if (mfaResponse.status === "success") {
              addToast(getTextByKey("sign-in-success"), {
                appearance: mfaResponse.status,
              });
              closeModal();

              if (onSubmitSuccess) {
                onSubmitSuccess(mfaResponse.message);
              }

              const currPage = getCurrentPage();
              if (currPage === "registration" || currPage === "login") {
                window.location.href = `${config.baseurl}${getPath()}home`;
              }
            }
          }
        };

        openMfaLoginModal(onMFACodeCheck, body.Email);
        return response;
      }

      handleCustomError(response.message);
    }

    return response;
  };

  return (
    <Form
      //@ts-ignore
      fieldsProps={{ label: "hidden", field: "square" }}
      fields={signInFields}
      onSubmit={handleSubmit}
      submitProps={{
        title: getTextByKey(submitTextKey),
      }}
      defaultValuesObj={{ UserName: emailField || "" }}
      buttonProps={{ buttonsSize: "large" }}
      exteranlRecaptchaRef={externalRecaptchaRef}
      className={clsx(styles.wrapper, className)}
      {...restProps}
    />
  );
});

export default LoginForm;
